import { action, computed, makeObservable, observable } from 'mobx';
import type { RootStore } from '.';
import { DragDropBase, DragStatus } from './dragdrop';

export class UiCardDragDropStore extends DragDropBase {
  private reorderingValid = false;
  private draggedCardIds: string[] = [];

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable<UiCardDragDropStore, 'reorderingValid' | 'draggedCardIds'>(this, {
      reorderingValid: observable,
      draggedCardIds: observable,
      cardDragStatus: computed,
      setReorderingValid: action.bound,
    });

    this.rootStore = rootStore;
  }

  get cardDragStatus(): DragStatus {
    return this.reorderingValid ? this.dragStatus : { targetId: this.dragStatus.targetId };
  }

  get isDraggingMultipleCards() {
    return this.draggedCardIds.length > 1;
  }

  get draggedCardsCount() {
    return this.draggedCardIds.length;
  }

  override startReordering(cardId: string) {
    super.startReordering(cardId);

    // Check if multiple cards are selected
    const { selectedCardIds } = this.rootStore.uiActiveSessionStore;
    const isCardSelected = selectedCardIds.includes(cardId);

    if (isCardSelected && selectedCardIds.length > 1) {
      // Multi-card drag: include all selected cards
      // Sort by queue order (top to bottom)
      const currentSession = this.rootStore.sessionsStore.currentSession!;
      this.draggedCardIds = selectedCardIds
        .map((id) => ({ id, index: currentSession.data.cardIds.indexOf(id) }))
        .filter(({ index }) => index !== -1)
        .sort((a, b) => a.index - b.index)
        .map(({ id }) => id);
    } else {
      // Single card drag
      this.draggedCardIds = [cardId];

      // Edge case: If a non-selected card is dragged and there are selected cards, deselect all cards
      if (!isCardSelected && selectedCardIds.length > 0) {
        this.rootStore.uiActiveSessionStore.deselectAllCards();
      }
    }
  }

  updateReordering(cardId: string, position: 'before' | 'after') {
    const currentSession = this.rootStore.sessionsStore.currentSession!;
    this._updateReordering(currentSession.data.cardIds, cardId, position);
  }

  override endReordering() {
    // Capture values before clearing drag status
    const reorderedCardId = this.dragStatus.targetId!;
    const targetCardId = this.dragStatus.overId!;
    const reorderPosition = this.dragStatus.position;
    const { reorderingValid, draggedCardIds } = this;
    const cardIdsToReorder = [...draggedCardIds];

    // Clear local state
    this.draggedCardIds = [];
    this.reorderingValid = false;

    // Clear drag status (base class behavior)
    super.endReordering();

    if (!targetCardId || !reorderingValid) {
      return;
    }

    // Use multi-card reorder if dragging multiple cards, otherwise use single-card reorder
    if (cardIdsToReorder.length > 1) {
      this.rootStore.sessionCardActions.reorderMultipleCards(
        cardIdsToReorder,
        targetCardId,
        reorderPosition!,
      );
    } else {
      this.rootStore.sessionCardActions.reorderCards(
        reorderedCardId,
        targetCardId,
        reorderPosition!,
      );
    }
  }

  setReorderingValid(isValid: boolean) {
    this.reorderingValid = isValid;
  }

  isCardBeingDragged = (cardId: string): boolean => {
    return this.draggedCardIds.includes(cardId);
  };
}
